import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, FileText, CalendarIcon, X, MoreVertical } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PermissionGuard } from "@/components/PermissionGuard";
import { toast } from "sonner";
import { formatDateTimeBR, parseISODateOrDateTime } from "@/lib/date-utils";
import { getActivePetWeights, sortPetWeightsDesc } from "@/lib/utils/pet-weight";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useCreatePetWeight,
  useDeletePetWeight,
  useUpdatePetWeight,
} from "@/hooks/usePetWeights";

type PetWeightRow = Tables<"pet_weights">;

export type PetWeightWithMeta = PetWeightRow & {
  source?: string | null;
  medical_record_id?: string | null;
};

const formSchema = z.object({
  weight: z.coerce.number().positive("Peso deve ser positivo").max(10000, "Peso máximo é 10000 kg"),
  measured_at: z.date(),
});

type FormValues = z.infer<typeof formSchema>;

const resolveSourceLabel = (weight: PetWeightWithMeta) => {
  const source = (weight.source ?? "").toLowerCase();
  if (weight.medical_record_id || source === "consultation") return "Consulta";
  if (source === "registration") return "Cadastro";
  if (source === "profile_update") return "Atualização do cadastro";
  return "Manual";
};

interface PetWeightHistoryCardProps {
  petId: string;
  weights: PetWeightWithMeta[];
  clearConsultationWeight?: (medicalRecordId: string) => Promise<void>;
}

export const PetWeightHistoryCard = ({
  petId,
  weights,
  clearConsultationWeight,
}: PetWeightHistoryCardProps) => {
  const { t } = useTranslation("patients");
  const createWeight = useCreatePetWeight();
  const updateWeight = useUpdatePetWeight();
  const deleteWeight = useDeletePetWeight();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PetWeightWithMeta | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PetWeightWithMeta | null>(null);

  const [deleteConsultDialogOpen, setDeleteConsultDialogOpen] = useState(false);
  const [deleteConsultTarget, setDeleteConsultTarget] = useState<PetWeightWithMeta | null>(null);

  const rows = useMemo(() => {
    return sortPetWeightsDesc(getActivePetWeights(weights));
  }, [weights]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      weight: undefined,
      measured_at: new Date(),
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ weight: undefined, measured_at: new Date() });
    setDialogOpen(true);
  };

  const openEdit = (row: PetWeightWithMeta) => {
    setEditing(row);
    const base = parseISODateOrDateTime(row.measured_at ?? row.created_at) ?? new Date();
    form.reset({
      weight: typeof row.weight === "number" ? row.weight : undefined,
      measured_at: base,
    });
    setDialogOpen(true);
  };

  const submit = async (values: FormValues) => {
    const measuredAtIso = values.measured_at.toISOString();

    if (editing) {
      await updateWeight.mutateAsync({
        id: editing.id,
        petId,
        patch: {
          weight: values.weight,
          measured_at: measuredAtIso,
        },
      });
      setDialogOpen(false);
      return;
    }

    await createWeight.mutateAsync({
      petId,
      weight: values.weight,
      measuredAt: measuredAtIso,
      source: "manual",
    });
    setDialogOpen(false);
  };

  const requestDelete = (row: PetWeightWithMeta) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteWeight.mutateAsync({ id: deleteTarget.id, petId });
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const requestDeleteConsult = (row: PetWeightWithMeta) => {
    setDeleteConsultTarget(row);
    setDeleteConsultDialogOpen(true);
  };

  const confirmDeleteConsult = async () => {
    const target = deleteConsultTarget;
    if (!target?.medical_record_id) return;
    if (!clearConsultationWeight) {
      toast.error(t("petWeight.invalidAction"));
      return;
    }
    await clearConsultationWeight(target.medical_record_id);
    setDeleteConsultDialogOpen(false);
    setDeleteConsultTarget(null);
  };

  return (
    <Card className="rounded-lg border !shadow-none">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-lg">{t("petWeight.title")}</CardTitle>
        <PermissionGuard resource="patients" action="edit" showAlert={false}>
          <Button variant="outline" size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("petWeight.add")}
          </Button>
        </PermissionGuard>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("petWeight.empty")}</p>
        ) : (
          <div className="max-h-[250px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead >{t("petWeight.columns.date")}</TableHead>
                  <TableHead >{t("petWeight.columns.weight")}</TableHead>
                  <TableHead className="text-right">{t("petWeight.columns.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody >
                {rows.map((row) => {
                  const isConsultation =
                    Boolean(row.medical_record_id) || (row.source ?? "").toLowerCase() === "consultation";
                  const sourceLabel = resolveSourceLabel(row);
                  const dateLabel = formatDateTimeBR(row.measured_at ?? row.created_at);
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">{dateLabel}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">
                        {typeof row.weight === "number" ? `${row.weight} kg` : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <PermissionGuard resource="patients" action="edit" showAlert={false}>
                          <div className="flex items-center justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label={t("petWeight.menu.label")}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {isConsultation ? (
                                  <>
                                    <DropdownMenuItem
                                      disabled={!row.medical_record_id}
                                      asChild={Boolean(row.medical_record_id)}
                                    >
                                      {row.medical_record_id ? (
                                        <Link to={`/medical-records/${row.medical_record_id}`}>
                                          <Pencil className="mr-2 h-4 w-4" />
                                          {t("petWeight.menu.edit")}
                                        </Link>
                                      ) : (
                                        <div className="flex items-center">
                                          <Pencil className="mr-2 h-4 w-4" />
                                          Editar
                                        </div>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      disabled={!row.medical_record_id}
                                      onClick={() => requestDeleteConsult(row)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      {t("petWeight.menu.delete")}
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <>
                                    <DropdownMenuItem onClick={() => openEdit(row)}>
                                      <Pencil className="mr-2 h-4 w-4" />
                                      {t("petWeight.menu.edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => requestDelete(row)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      {t("petWeight.menu.delete")}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </PermissionGuard>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditing(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t("petWeight.dialog.editTitle") : t("petWeight.dialog.addTitle")}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(submit)}>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("petWeight.dialog.weight")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          placeholder="Ex: 12.5"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="measured_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("petWeight.dialog.dateTime")}</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              {field.value ? (
                                format(field.value, "dd/MM/yyyy 'às' HH:mm")
                              ) : (
                                <span>{t("petWeight.dialog.selectDateTime")}</span>
                              )}
                              {field.value ? (
                                <X
                                  className="ml-auto h-4 w-4 opacity-50 hover:opacity-100"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    field.onChange(new Date());
                                  }}
                                />
                              ) : (
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <div className="flex flex-col gap-4 p-3 sm:flex-row sm:items-start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                  if (!date) {
                                    field.onChange(new Date());
                                    return;
                                  }

                                  const currentValue = field.value ?? new Date();
                                  const updated = new Date(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate(),
                                    currentValue.getHours(),
                                    currentValue.getMinutes(),
                                    0,
                                    0,
                                  );

                                  field.onChange(updated);
                                }}
                                disabled={(date) => date > new Date()}
                                initialFocus
                              />
                              <div className="flex min-w-[160px] flex-col gap-2">
                                <p className="text-sm font-medium text-foreground">{t("petWeight.dialog.time")}</p>
                                <Input
                                  type="time"
                                  value={field.value ? format(field.value, "HH:mm") : ""}
                                  onChange={(event) => {
                                    const timeValue = event.target.value;
                                    if (!timeValue) return;
                                    const [hours, minutes] = timeValue.split(":").map(Number);
                                    const base = field.value ?? new Date();
                                    const updated = new Date(base);
                                    updated.setHours(hours, minutes, 0, 0);
                                    field.onChange(updated);
                                  }}
                                />
                                <Button
                                  variant="outline"
                                  type="button"
                                  onClick={() => {
                                    field.onChange(new Date());
                                  }}
                                >
                                  {t("petWeight.dialog.useCurrentTime")}
                                </Button>
                                <Button
                                  variant="ghost"
                                  type="button"
                                  onClick={() => {
                                    field.onChange(new Date());
                                  }}
                                >
                                  {t("petWeight.dialog.clear")}
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={createWeight.isPending || updateWeight.isPending}
                >
                  {t("petWeight.dialog.cancel")}
                </Button>
                <Button type="submit" disabled={createWeight.isPending || updateWeight.isPending}>
                  {t("petWeight.dialog.save")}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("petWeight.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("petWeight.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteWeight.isPending}
              onClick={() => {
                setDeleteTarget(null);
              }}
            >
              {t("petWeight.dialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteWeight.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {t("petWeight.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConsultDialogOpen} onOpenChange={setDeleteConsultDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("petWeight.deleteConsult.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("petWeight.deleteConsult.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteConsultTarget(null)}
            >
              {t("petWeight.dialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void confirmDeleteConsult();
              }}
            >
              {t("petWeight.deleteConsult.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
